import React from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  Edit3,
  Lock,
  Menu,
  Play,
  Trophy,
  X,
} from 'lucide-react';

// ISO code mapping for flag-icons library.
// Entries for ids no longer in teams.json are kept so migrated pool data still
// renders correctly.
export const ISO_MAP = {
  can: 'ca', col: 'co', pol: 'pl', jpn: 'jp',
  usa: 'us', ger: 'de', nga: 'ng', aus: 'au',
  mex: 'mx', ita: 'it', cmr: 'cm', irn: 'ir',
  fra: 'fr', uru: 'uy', tur: 'tr', kor: 'kr',
  bra: 'br', bel: 'be', egy: 'eg', ksa: 'sa',
  por: 'pt', ned: 'nl', alg: 'dz', per: 'pe',
  esp: 'es', sui: 'ch', mar: 'ma', pan: 'pa',
  eng: 'gb-eng', den: 'dk', ecu: 'ec', nzl: 'nz',
  arg: 'ar', aut: 'at', sen: 'sn', crc: 'cr',
  cro: 'hr', ukr: 'ua', civ: 'ci', hon: 'hn',
  swe: 'se', sco: 'gb-sct', gha: 'gh', chi: 'cl',
  wal: 'gb-wls', cze: 'cz', tun: 'tn', jam: 'jm',
  rsa: 'za', bih: 'ba', qat: 'qa', hai: 'ht',
  par: 'py', cuw: 'cw', irq: 'iq', nor: 'no',
  jor: 'jo', cod: 'cd', uzb: 'uz', cpv: 'cv',
};

// ============================================================================
// Primitives & Utility Components
// ============================================================================

export const Flag = ({ iso, teamId, size = 32, radius = 4, style }) => {
  const code = iso || ISO_MAP[teamId] || teamId;
  if (!code) return null;
  const src = `https://cdn.jsdelivr.net/gh/lipis/flag-icons@7.2.3/flags/4x3/${code.toLowerCase()}.svg`;
  return (
    <img
      src={src}
      alt=""
      style={{
        width: size,
        height: size * 0.75,
        objectFit: 'cover',
        borderRadius: radius,
        display: 'inline-block',
        verticalAlign: 'middle',
        boxShadow: 'inset 0 0 0 0.5px rgba(0,0,0,0.1)',
        ...style,
      }}
      onError={(e) => {
        e.target.style.display = 'none';
      }}
    />
  );
};

// Map kebab-case names to lucide-react components, preserving the existing
// <Icon name="..." /> API used throughout the codebase.
const ICON_MAP = {
  'alert-triangle': AlertTriangle,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  'check': Check,
  'chevron-down': ChevronDown,
  'chevron-up': ChevronUp,
  'copy': Copy,
  'edit': Edit3,
  'lock': Lock,
  'menu': Menu,
  'play': Play,
  'trophy': Trophy,
  'x': X,
};

export const Icon = ({ name, size = 16, color = 'currentColor', strokeWidth = 2 }) => {
  const Component = ICON_MAP[name];
  if (!Component) return null;
  return (
    <Component
      size={size}
      color={color}
      strokeWidth={strokeWidth}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
    />
  );
};

export const Stars = ({ value, size = 14, color }) => {
  const c = color || (value === 5 ? 'var(--gold)' : 'var(--blue)');
  return (
    <span style={{ fontFamily: 'var(--font-mono)', fontSize: size, color: c, letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>
      {'★'.repeat(value) + '☆'.repeat(5 - value)}
    </span>
  );
};

export const Chip = ({ children, tone = 'neutral', style }) => {
  const tones = {
    neutral: { background: 'transparent',     color: 'var(--ink)',     borderColor: 'var(--hairline-strong)' },
    blue:    { background: 'var(--blue)',     color: 'var(--paper)',   borderColor: 'var(--blue)' },
    ink:     { background: 'var(--ink)',      color: 'var(--paper)',   borderColor: 'var(--ink)' },
    pale:    { background: 'var(--blue-pale)',color: 'var(--blue-deep)', borderColor: 'transparent' },
    success: { background: 'rgba(17,166,90,0.10)',  color: 'var(--success)', borderColor: 'rgba(17,166,90,0.32)' },
    warning: { background: 'rgba(229,138,0,0.10)',  color: 'var(--warning)', borderColor: 'rgba(229,138,0,0.32)' },
    danger:  { background: 'rgba(214,35,62,0.08)',  color: 'var(--danger)',  borderColor: 'rgba(214,35,62,0.32)' },
    gold:    { background: 'rgba(255,184,28,0.14)', color: '#9A6F00',        borderColor: 'rgba(255,184,28,0.45)' },
  };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '7px 12px', borderRadius: 999,
      fontFamily: 'var(--font-display)', fontSize: 12,
      letterSpacing: '0.08em', textTransform: 'uppercase',
      border: '1.5px solid', lineHeight: 1, ...tones[tone], ...style,
    }}>{children}</span>
  );
};

export const Button = ({ variant = 'primary', children, onClick, type = 'button', disabled, style, ...rest }) => {
  const base = {
    fontFamily: 'var(--font-display)',
    fontWeight: 400,
    fontSize: 16,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    borderRadius: 999,
    padding: '14px 26px',
    border: '1.5px solid transparent',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 200ms var(--ease-out)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    opacity: disabled ? 0.4 : 1,
    lineHeight: 1,
  };
  const variants = {
    primary:   { background: 'var(--ink)',  color: 'var(--paper)' },
    blue:      { background: 'var(--blue)', color: 'var(--paper)' },
    secondary: { background: 'transparent', color: 'var(--ink)', borderColor: 'var(--ink)' },
    ghost:     { background: 'transparent', color: 'var(--blue)' },
    onInk:     { background: 'var(--paper)', color: 'var(--ink)' },
    danger:    { background: 'transparent', color: 'var(--danger)', borderColor: 'var(--danger)' },
    large:     { background: 'var(--ink)', color: 'var(--paper)', padding: '20px 40px', fontSize: 20 },
  };
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      style={{ ...base, ...variants[variant], ...style }}
      {...rest}
    >
      {children}
    </button>
  );
};

export const Field = ({ label, value, onChange, type = 'text', placeholder, hint, error }) => {
  const [focused, setFocused] = React.useState(false);
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span style={{
        fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.1em',
        textTransform: 'uppercase', color: 'var(--ink)',
      }}>{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          background: 'var(--paper)',
          border: `1.5px solid ${error ? 'var(--danger)' : focused ? 'var(--blue)' : 'var(--hairline-strong)'}`,
          borderRadius: 8,
          padding: '14px 16px',
          fontFamily: 'var(--font-body)',
          fontSize: 16,
          color: 'var(--ink)',
          outline: 'none',
          transition: 'all 180ms var(--ease-out)',
          boxShadow: focused && !error ? '0 0 0 4px rgba(47,79,252,0.12)' : 'none',
        }}
      />
      {error && <span style={{ fontSize: 13, color: 'var(--danger)' }}>{error}</span>}
      {!error && hint && <span style={{ fontSize: 13, color: 'var(--ink-soft)' }}>{hint}</span>}
    </label>
  );
};

// Tracks whether the viewport is phone-width. Used to swap the inline nav for a
// hamburger menu so the header stays a single row instead of wrapping to 3 lines.
export const useIsMobile = (query = '(max-width: 768px)') => {
  const get = () => typeof window !== 'undefined' && window.matchMedia(query).matches;
  const [isMobile, setIsMobile] = React.useState(get);
  React.useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return isMobile;
};

// ============================================================================
// Layout: Header Component
// ============================================================================

const NAV_LINKS = [
  ['dashboard',  'DASHBOARD'],
  ['predictor',  'PREDICTOR'],
  ['eliminator', 'ELIMINATOR', true],
  ['bracket',    'BRACKET'],
  ['rules',      'RULES'],
];

export const Header = ({ poolId, screen, onNav }) => {
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = React.useState(false);

  // Close the dropdown whenever we navigate or grow back to desktop width.
  React.useEffect(() => { setMenuOpen(false); }, [screen, isMobile]);

  const handleNav = (key) => { setMenuOpen(false); onNav(key); };

  const wordmark = (
    <a
      onClick={() => handleNav('hero')}
      style={{
        fontFamily: 'var(--font-display)', fontSize: 28, textTransform: 'uppercase',
        letterSpacing: '-0.005em', cursor: 'pointer', color: 'var(--ink)',
        borderBottom: 'none', lineHeight: 1,
      }}
    >
      SWEEP<span style={{ color: 'var(--blue)' }}>:</span><span style={{ color: 'var(--gold)' }}>WC26</span>
    </a>
  );

  const navLink = (key, label, isNew, asBlock) => (
    <a
      key={key}
      onClick={() => handleNav(key)}
      style={{
        fontFamily: 'var(--font-display)', fontSize: 15, letterSpacing: '0.06em',
        textTransform: 'uppercase', cursor: 'pointer',
        color: screen === key ? 'var(--blue)' : 'var(--ink)',
        borderBottom: 'none',
        display: asBlock ? 'flex' : 'inline-flex', alignItems: 'center', gap: 7,
        ...(asBlock && { padding: '12px 0', width: '100%' }),
      }}
    >
      {label}
      {isNew && (
        <span style={{
          background: 'var(--gold)', color: 'var(--ink)',
          fontSize: 9, letterSpacing: '0.12em',
          padding: '2px 6px', borderRadius: 999, lineHeight: 1,
        }}>NEW</span>
      )}
    </a>
  );

  const exitLink = (asBlock) => (
    <a
      onClick={() => handleNav('exit')}
      style={{
        fontFamily: 'var(--font-display)', fontSize: 15, letterSpacing: '0.06em',
        textTransform: 'uppercase', cursor: 'pointer', color: 'var(--danger)',
        borderBottom: 'none',
        ...(asBlock ? { padding: '12px 0', width: '100%' } : { marginLeft: 12 }),
      }}
    >
      EXIT POOL
    </a>
  );

  const poolChip = (
    <span style={{
      fontFamily: 'var(--font-mono)', fontSize: 13, padding: '8px 14px',
      background: 'var(--blue-pale)', borderRadius: 999, color: 'var(--blue-deep)',
      letterSpacing: '0.06em', whiteSpace: 'nowrap',
    }}>POOL · {poolId?.toUpperCase()}</span>
  );

  return (
    <header style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
      background: 'rgba(255,255,255,0.92)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      borderBottom: '1px solid var(--hairline)',
    }}>
      <div style={{
        maxWidth: 1440, margin: '0 auto',
        padding: 'clamp(12px, 3vw, 18px) clamp(16px, 4vw, 32px)',
        display: 'flex', alignItems: 'center', gap: 'clamp(12px, 3vw, 32px)',
        justifyContent: 'space-between',
      }}>
        {/* Desktop: wordmark + inline nav. Mobile: wordmark + hamburger. */}
        {!isMobile ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(12px, 3vw, 32px)' }}>
              {wordmark}
              {poolId && (
                <nav style={{ display: 'flex', gap: 'clamp(10px, 2.5vw, 28px)', alignItems: 'center' }}>
                  {NAV_LINKS.map(([key, label, isNew]) => navLink(key, label, isNew, false))}
                  {exitLink(false)}
                </nav>
              )}
            </div>
            {poolId && poolChip}
          </>
        ) : (
          <>
            {wordmark}
            {poolId && (
              <button
                onClick={() => setMenuOpen(o => !o)}
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 44, height: 44, borderRadius: 10, cursor: 'pointer',
                  background: 'transparent', border: '1.5px solid var(--hairline-strong)',
                  color: 'var(--ink)',
                }}
              >
                <Icon name={menuOpen ? 'x' : 'menu'} size={22} />
              </button>
            )}
          </>
        )}
      </div>

      {/* Mobile dropdown panel — nav links, exit, and pool chip stacked. */}
      {isMobile && poolId && menuOpen && (
        <nav style={{
          borderTop: '1px solid var(--hairline)',
          background: 'rgba(255,255,255,0.98)',
          padding: '8px clamp(16px, 4vw, 32px) 16px',
          display: 'flex', flexDirection: 'column',
        }}>
          {NAV_LINKS.map(([key, label, isNew]) => navLink(key, label, isNew, true))}
          {exitLink(true)}
          <div style={{ marginTop: 12 }}>{poolChip}</div>
        </nav>
      )}
    </header>
  );
};

// ============================================================================
// UI component: TeamCard
// ============================================================================

export const TeamCard = ({ team, assignedTo, highlighted, onClick }) => {
  const [hover, setHover] = React.useState(false);
  const isMine = highlighted;
  const isAssigned = !!assignedTo;

  let bg = 'var(--paper)';
  let fg = 'var(--ink)';
  let labelColor = 'var(--blue)';
  let borderColor = 'var(--hairline-strong)';

  if (isMine) {
    bg = 'var(--blue)';
    fg = 'var(--paper)';
    labelColor = 'rgba(255,255,255,0.85)';
    borderColor = 'var(--blue)';
  } else if (hover) {
    borderColor = 'var(--blue)';
  }

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: bg, color: fg,
        border: `2px solid ${borderColor}`,
        borderRadius: 12,
        padding: 18,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        cursor: 'pointer',
        transition: 'all 200ms var(--ease-out)',
        position: 'relative',
        opacity: !isAssigned && !hover ? 0.85 : 1,
        transform: hover && !isMine ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hover && !isMine ? '0 8px 24px rgba(16,21,79,0.08)' : 'none',
        minHeight: 160
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Flag teamId={team.id} size={48} radius={4} />
        <span style={{
          fontFamily: 'var(--font-display)', fontSize: 12,
          letterSpacing: '0.12em', textTransform: 'uppercase',
          color: labelColor,
          background: isMine ? 'rgba(255,255,255,0.18)' : 'transparent',
          padding: isMine ? '4px 8px' : '0',
          borderRadius: 4,
        }}>GRP {team.group}</span>
      </div>

      <div>
        <div style={{
          fontFamily: 'var(--font-display)', fontSize: 24,
          letterSpacing: '-0.005em', textTransform: 'uppercase',
          lineHeight: 1, color: fg,
        }}>{team.country}</div>
      </div>

      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginTop: 'auto', paddingTop: 8,
        borderTop: `1px solid ${isMine ? 'rgba(255,255,255,0.2)' : 'var(--hairline)'}`,
      }}>
        <Stars value={team.strength} color={isMine ? '#FFB81C' : (team.strength === 5 ? 'var(--gold)' : 'var(--blue)')} />
        {isAssigned ? (
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: 11,
            letterSpacing: '0.08em', textTransform: 'uppercase',
            color: isMine ? 'var(--paper)' : 'var(--ink)',
            fontWeight: 700,
          }}>{assignedTo}</span>
        ) : (
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: 11,
            letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-mute)',
          }}>OPEN</span>
        )}
      </div>
    </div>
  );
};
